import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PasswordInput } from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  CircleNotch,
  Shield,
  ShieldCheck,
  ShieldWarning,
  Lock,
  LockOpen,
  Key,
  Users,
  UserCircle,
  Clock,
  Warning,
  Check,
  X,
  Plus,
  Trash,
  PencilSimple,
  Eye,
  Desktop,
  Globe,
  Pulse,
  SignOut,
  Password,
  Gear,
  CaretRight,
  Info,
  CurrencyDollar,
  ChartLineUp,
  Package,
  ChartBar,
  Database,
  CloudArrowUp,
  CloudArrowDown,
  Alarm,
  DownloadSimple,
  Files
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module info for role permissions
const MODULES = [
  { id: 'hr', name: 'HR & Employees', icon: Users },
  { id: 'accounts', name: 'Accounts', icon: CurrencyDollar },
  { id: 'sales', name: 'Sales', icon: ChartLineUp },
  { id: 'users', name: 'Users', icon: UserCircle },
  { id: 'roles', name: 'Roles', icon: Shield },
  { id: 'settings', name: 'Settings', icon: Gear },
  { id: 'inventory', name: 'Inventory', icon: Package },
  { id: 'compliance', name: 'Compliance', icon: ShieldCheck },
  { id: 'reports', name: 'Reports', icon: ChartBar },
  { id: 'audit', name: 'Audit', icon: Eye }
];

const PERMISSIONS = ['view', 'create', 'edit', 'delete'];

const ROLE_COLORS = {
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30',
  blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

export default function SecuritySettingsPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  
  // Custom Roles state
  const [customRoles, setCustomRoles] = useState([]);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    color: 'blue',
    permissions: {}
  });
  
  // Security Settings state
  const [securitySettings, setSecuritySettings] = useState(null);
  const [settingsChanged, setSettingsChanged] = useState(false);
  
  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  
  // Users for lock/unlock
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [requireChangeOnLogin, setRequireChangeOnLogin] = useState(true);
  const [lockReason, setLockReason] = useState('');
  
  // Security Phase 2 State
  const [encryptionStatus, setEncryptionStatus] = useState([]);
  const [backups, setBackups] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [encryptingData, setEncryptingData] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Fetch functions
  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch security dashboard:', err);
    }
  };

  const fetchCustomRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/custom-roles`, { headers });
      setCustomRoles(res.data.roles || []);
    } catch (err) {
      console.error('Failed to fetch custom roles:', err);
    }
  };

  const fetchSecuritySettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/settings`, { headers });
      setSecuritySettings(res.data);
    } catch (err) {
      console.error('Failed to fetch security settings:', err);
    }
  };

  const fetchSessions = async () => {
    try {
      const [myRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/security/sessions`, { headers }),
        isAdmin ? axios.get(`${API_URL}/api/security/sessions/all`, { headers }) : Promise.resolve({ data: { sessions: [] } })
      ]);
      setSessions(myRes.data.sessions || []);
      setAllSessions(allRes.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`, { headers });
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  // Security Phase 2 fetch functions
  const fetchEncryptionStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/encryption/status`, { headers });
      setEncryptionStatus(res.data.encryption_status || []);
    } catch (err) {
      console.error('Failed to fetch encryption status:', err);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/backup/list`, { headers });
      setBackups(res.data.backups || []);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/suspicious-activity/alerts?days=7`, { headers });
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  };

  const fetchAuditSummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/security/audit/summary?days=30`, { headers });
      setAuditSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch audit summary:', err);
    }
  };

  // Phase 2 action handlers
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const res = await axios.post(`${API_URL}/api/security/backup/create`, {
        compress: true,
        include_attachments: false
      }, { headers });
      toast.success('Backup created successfully');
      fetchBackups();
    } catch (err) {
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    try {
      await axios.delete(`${API_URL}/api/security/backup/${backupId}`, { headers });
      toast.success('Backup deleted');
      fetchBackups();
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };

  const handleBulkEncrypt = async () => {
    if (!window.confirm('This will encrypt all sensitive data fields. Continue?')) return;
    setEncryptingData(true);
    try {
      const res = await axios.post(`${API_URL}/api/security/encrypt/bulk`, {}, { headers });
      toast.success('Encryption process started');
      // Refresh status after a delay
      setTimeout(() => {
        fetchEncryptionStatus();
        setEncryptingData(false);
      }, 3000);
    } catch (err) {
      toast.error('Failed to start encryption');
      setEncryptingData(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchCustomRoles(),
        fetchSecuritySettings(),
        fetchSessions(),
        isAdmin ? fetchUsers() : Promise.resolve(),
        isAdmin ? fetchEncryptionStatus() : Promise.resolve(),
        isAdmin ? fetchBackups() : Promise.resolve(),
        isAdmin ? fetchAlerts() : Promise.resolve(),
        isAdmin ? fetchAuditSummary() : Promise.resolve()
      ]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Role CRUD
  const handleSaveRole = async () => {
    try {
      if (editingRole) {
        await axios.patch(
          `${API_URL}/api/security/custom-roles/${editingRole.id}`,
          roleForm,
          { headers }
        );
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API_URL}/api/security/custom-roles`, roleForm, { headers });
        toast.success('Role created successfully');
      }
      setIsRoleDialogOpen(false);
      setEditingRole(null);
      setRoleForm({ name: '', description: '', color: 'blue', permissions: {} });
      fetchCustomRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await axios.delete(`${API_URL}/api/security/custom-roles/${roleId}`, { headers });
      toast.success('Role deleted');
      fetchCustomRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description,
      color: role.color,
      permissions: role.permissions || {}
    });
    setIsRoleDialogOpen(true);
  };

  const openNewRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', color: 'blue', permissions: {} });
    setIsRoleDialogOpen(true);
  };

  // Permission toggle
  const togglePermission = (module, permission) => {
    setRoleForm(prev => {
      const modulePerms = prev.permissions[module] || [];
      const newPerms = modulePerms.includes(permission)
        ? modulePerms.filter(p => p !== permission)
        : [...modulePerms, permission];
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [module]: newPerms
        }
      };
    });
  };

  // Security Settings
  const handleSaveSecuritySettings = async () => {
    try {
      await axios.patch(`${API_URL}/api/security/settings`, securitySettings, { headers });
      toast.success('Security settings saved');
      setSettingsChanged(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  const updateSetting = (key, value) => {
    setSecuritySettings(prev => ({ ...prev, [key]: value }));
    setSettingsChanged(true);
  };

  // Session management
  const handleTerminateSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/api/security/sessions/${sessionId}`, { headers });
      toast.success('Session terminated');
      fetchSessions();
    } catch (err) {
      toast.error('Failed to terminate session');
    }
  };

  const handleTerminateAllSessions = async (userId = null) => {
    if (!window.confirm('Are you sure you want to terminate all sessions?')) return;
    try {
      const url = userId 
        ? `${API_URL}/api/security/sessions/terminate-all?user_id=${userId}`
        : `${API_URL}/api/security/sessions/terminate-all`;
      await axios.post(url, {}, { headers });
      toast.success('All sessions terminated');
      fetchSessions();
    } catch (err) {
      toast.error('Failed to terminate sessions');
    }
  };

  // Lock/Unlock user
  const handleLockUser = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(
        `${API_URL}/api/security/users/${selectedUser.id}/lock`,
        { action: 'lock', reason: lockReason },
        { headers }
      );
      toast.success(`Account ${selectedUser.email} locked`);
      setLockDialogOpen(false);
      setSelectedUser(null);
      setLockReason('');
      fetchUsers();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock account');
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await axios.post(
        `${API_URL}/api/security/users/${userId}/lock`,
        { action: 'unlock' },
        { headers }
      );
      toast.success('Account unlocked');
      fetchUsers();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock account');
    }
  };

  // Password reset
  const handlePasswordReset = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await axios.post(
        `${API_URL}/api/security/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword, require_change_on_login: requireChangeOnLogin },
        { headers }
      );
      toast.success(`Password reset for ${selectedUser.email}`);
      setPasswordResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" weight="duotone" />
            Security & Data Protection
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage roles, access control, and security settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 max-w-5xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Pulse className="w-4 h-4" />
            <span className="hidden sm:inline">Dashboard</span>
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Roles</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-2">
            <Desktop className="w-4 h-4" />
            <span className="hidden sm:inline">Sessions</span>
          </TabsTrigger>
          <TabsTrigger value="encryption" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span className="hidden sm:inline">Encryption</span>
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            <span className="hidden sm:inline">Backup</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Alarm className="w-4 h-4" />
            <span className="hidden sm:inline">Alerts</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Gear className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.total_users || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Desktop className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.active_sessions || 0}</p>
                  <p className="text-xs text-muted-foreground">Active Sessions</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Lock className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.locked_accounts || 0}</p>
                  <p className="text-xs text-muted-foreground">Locked Accounts</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <Warning className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.failed_logins_24h || 0}</p>
                  <p className="text-xs text-muted-foreground">Failed Logins (24h)</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Recent Security Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pulse className="w-5 h-5 text-primary" />
                Recent Security Events
              </CardTitle>
              <CardDescription>Latest security-related activities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.recent_events?.length > 0 ? (
                  dashboard.recent_events.map((event, idx) => (
                    <div 
                      key={event.id || idx}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          event.action?.includes('LOCK') ? 'bg-red-500/20' :
                          event.action?.includes('LOGIN') ? 'bg-green-500/20' :
                          event.action?.includes('PASSWORD') ? 'bg-orange-500/20' :
                          'bg-blue-500/20'
                        }`}>
                          {event.action?.includes('LOCK') ? <Lock className="w-4 h-4 text-red-400" /> :
                           event.action?.includes('LOGIN') ? <Check className="w-4 h-4 text-green-400" /> :
                           event.action?.includes('PASSWORD') ? <Key className="w-4 h-4 text-orange-400" /> :
                           <Shield className="w-4 h-4 text-blue-400" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{event.action}</p>
                          <p className="text-xs text-muted-foreground">{event.user_email}</p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(event.timestamp)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No recent security events</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Roles Tab */}
        <TabsContent value="roles" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Custom Roles</h2>
              <p className="text-sm text-muted-foreground">
                Create and manage custom roles with specific permissions
              </p>
            </div>
            {isAdmin && (
              <Button onClick={openNewRole} data-testid="create-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customRoles.map((role) => (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-card border border-border rounded-xl p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${ROLE_COLORS[role.color] || ROLE_COLORS.blue}`}>
                      <Shield className="w-5 h-5" weight="duotone" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{role.name}</h3>
                      <Badge variant="outline" className={`text-xs ${ROLE_COLORS[role.color] || ROLE_COLORS.blue}`}>
                        Custom Role
                      </Badge>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditRole(role)}
                        data-testid={`edit-role-${role.id}`}
                      >
                        <PencilSimple className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteRole(role.id)}
                        data-testid={`delete-role-${role.id}`}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">{role.description}</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(role.permissions || {}).map(([module, perms]) => (
                    perms.length > 0 && (
                      <Badge key={module} variant="secondary" className="text-xs">
                        {module}: {perms.length}
                      </Badge>
                    )
                  ))}
                </div>
              </motion.div>
            ))}
            
            {customRoles.length === 0 && (
              <div className="col-span-full text-center py-12">
                <ShieldCheck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No custom roles created yet</p>
                {isAdmin && (
                  <Button className="mt-4" onClick={openNewRole}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Role
                  </Button>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* User Access Tab */}
        <TabsContent value="users" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>User Access Management</CardTitle>
              <CardDescription>Lock/unlock accounts and reset passwords</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <UserCircle className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{u.first_name} {u.last_name}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{u.role}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          {u.is_active ? (
                            <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-400">
                              <Lock className="w-3 h-3 mr-1" />
                              Locked
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            {isAdmin && u.id !== user?.id && (
                              <>
                                {u.is_active ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-400 hover:text-red-300"
                                    onClick={() => {
                                      setSelectedUser(u);
                                      setLockDialogOpen(true);
                                    }}
                                    data-testid={`lock-user-${u.id}`}
                                  >
                                    <Lock className="w-4 h-4 mr-1" />
                                    Lock
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-400 hover:text-green-300"
                                    onClick={() => handleUnlockUser(u.id)}
                                    data-testid={`unlock-user-${u.id}`}
                                  >
                                    <LockOpen className="w-4 h-4 mr-1" />
                                    Unlock
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setPasswordResetDialogOpen(true);
                                  }}
                                  data-testid={`reset-pwd-${u.id}`}
                                >
                                  <Key className="w-4 h-4 mr-1" />
                                  Reset Password
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-6">
          <div className="space-y-6">
            {/* My Sessions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Desktop className="w-5 h-5 text-primary" />
                      My Active Sessions
                    </CardTitle>
                    <CardDescription>Devices where you're currently logged in</CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTerminateAllSessions()}
                    data-testid="terminate-all-my-sessions"
                  >
                    <SignOut className="w-4 h-4 mr-2" />
                    Sign Out All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div 
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/20">
                          <Globe className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {session.user_agent?.substring(0, 50)}...
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{session.ip_address || 'Unknown IP'}</span>
                            <span>•</span>
                            <span>Last active: {formatDate(session.last_activity)}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleTerminateSession(session.id)}
                        data-testid={`terminate-session-${session.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {sessions.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No active sessions</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* All Company Sessions (Admin only) */}
            {isAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    All Company Sessions
                  </CardTitle>
                  <CardDescription>Active sessions across all users</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allSessions.map((session) => (
                      <div 
                        key={session.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-blue-500/20">
                            <UserCircle className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{session.user_name || session.user_email}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{session.ip_address || 'Unknown IP'}</span>
                              <span>•</span>
                              <span>{formatDate(session.last_activity)}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleTerminateSession(session.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {allSessions.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">No active sessions</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Security Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Gear className="w-5 h-5 text-primary" />
                    Security Settings
                  </CardTitle>
                  <CardDescription>Configure login protection and session policies</CardDescription>
                </div>
                {settingsChanged && isAdmin && (
                  <Button onClick={handleSaveSecuritySettings} data-testid="save-security-settings">
                    <Check className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Login Protection */}
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Login Protection
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Failed Login Attempts</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={securitySettings?.max_failed_attempts || 5}
                      onChange={(e) => updateSetting('max_failed_attempts', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                    <p className="text-xs text-muted-foreground">
                      Account will be locked after this many failed attempts
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Lockout Duration (minutes)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={1440}
                      value={securitySettings?.lockout_duration_minutes || 30}
                      onChange={(e) => updateSetting('lockout_duration_minutes', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                    <p className="text-xs text-muted-foreground">
                      How long the account stays locked
                    </p>
                  </div>
                </div>
              </div>

              {/* Session Settings */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Session Settings
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Session Timeout (minutes)</Label>
                    <Input
                      type="number"
                      min={15}
                      max={1440}
                      value={securitySettings?.session_timeout_minutes || 480}
                      onChange={(e) => updateSetting('session_timeout_minutes', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                    <p className="text-xs text-muted-foreground">
                      Idle sessions expire after this duration
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Max Concurrent Sessions</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={securitySettings?.max_concurrent_sessions || 3}
                      onChange={(e) => updateSetting('max_concurrent_sessions', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum active sessions per user
                    </p>
                  </div>
                </div>
              </div>

              {/* Password Policy */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Password Policy
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Minimum Password Length</Label>
                    <Input
                      type="number"
                      min={6}
                      max={32}
                      value={securitySettings?.min_password_length || 8}
                      onChange={(e) => updateSetting('min_password_length', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Password Expiry (days)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={365}
                      value={securitySettings?.require_password_change_days || 90}
                      onChange={(e) => updateSetting('require_password_change_days', parseInt(e.target.value))}
                      disabled={!isAdmin}
                    />
                    <p className="text-xs text-muted-foreground">
                      Set to 0 to disable password expiry
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={securitySettings?.require_special_chars || false}
                      onCheckedChange={(checked) => updateSetting('require_special_chars', checked)}
                      disabled={!isAdmin}
                    />
                    <Label>Require Special Characters</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={securitySettings?.require_numbers || false}
                      onCheckedChange={(checked) => updateSetting('require_numbers', checked)}
                      disabled={!isAdmin}
                    />
                    <Label>Require Numbers</Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={securitySettings?.require_uppercase || false}
                      onCheckedChange={(checked) => updateSetting('require_uppercase', checked)}
                      disabled={!isAdmin}
                    />
                    <Label>Require Uppercase</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Encryption Tab */}
        <TabsContent value="encryption" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-primary" weight="duotone" />
                    Data Encryption
                  </CardTitle>
                  <CardDescription>Encrypt sensitive data fields for enhanced security</CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={handleBulkEncrypt} disabled={encryptingData} data-testid="bulk-encrypt-btn">
                    {encryptingData ? (
                      <><CircleNotch className="w-4 h-4 mr-2 animate-spin" /> Encrypting...</>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" /> Encrypt All Data</>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {encryptionStatus.map((item, idx) => (
                    <div key={idx} className="p-4 border rounded-lg bg-muted/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{item.collection}</span>
                        <Badge variant={item.encryption_percentage === 100 ? "default" : "secondary"}>
                          {item.encryption_percentage}%
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">Field: {item.field}</p>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary rounded-full h-2 transition-all duration-500"
                          style={{ width: `${item.encryption_percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.encrypted_count} of {item.documents_with_data} encrypted
                      </p>
                    </div>
                  ))}
                </div>
                {encryptionStatus.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Lock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No sensitive fields to encrypt</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-primary" weight="duotone" />
                    Backup & Recovery
                  </CardTitle>
                  <CardDescription>Create and manage data backups</CardDescription>
                </div>
                {isAdmin && (
                  <Button onClick={handleCreateBackup} disabled={creatingBackup} data-testid="create-backup-btn">
                    {creatingBackup ? (
                      <><CircleNotch className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                    ) : (
                      <><CloudArrowUp className="w-4 h-4 mr-2" /> Create Backup</>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {backups.length > 0 ? (
                  <div className="divide-y divide-border rounded-lg border">
                    {backups.map((backup) => (
                      <div key={backup.id} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Files className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{backup.name || backup.filename || 'Backup'}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(backup.created_at).toLocaleString()} • {formatBytes(backup.size_bytes || backup.file_size || 0)}
                              {backup.compressed && <Badge variant="outline" className="ml-2">Compressed</Badge>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => toast.info('Download feature coming soon')}>
                            <DownloadSimple className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteBackup(backup.id)}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Database className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No backups yet</p>
                    <p className="text-sm">Create your first backup to protect your data</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Alarm className="w-5 h-5 text-primary" weight="duotone" />
                    Security Alerts
                  </CardTitle>
                  <CardDescription>Suspicious activity detection (Last 7 days)</CardDescription>
                </div>
                <Button variant="outline" onClick={fetchAlerts}>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {alerts.length > 0 ? (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div 
                        key={alert.id} 
                        className={`p-4 border rounded-lg ${
                          alert.severity === 'high' ? 'border-red-500/50 bg-red-500/5' :
                          alert.severity === 'medium' ? 'border-amber-500/50 bg-amber-500/5' :
                          'border-border bg-muted/20'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${
                              alert.severity === 'high' ? 'bg-red-500/20' :
                              alert.severity === 'medium' ? 'bg-amber-500/20' :
                              'bg-muted'
                            }`}>
                              <Warning className={`w-5 h-5 ${
                                alert.severity === 'high' ? 'text-red-500' :
                                alert.severity === 'medium' ? 'text-amber-500' :
                                'text-muted-foreground'
                              }`} />
                            </div>
                            <div>
                              <p className="font-medium">{alert.description}</p>
                              <p className="text-sm text-muted-foreground capitalize">
                                Type: {alert.type.replace(/_/g, ' ')}
                              </p>
                              {alert.details?.email && (
                                <p className="text-sm text-muted-foreground">
                                  Email: {alert.details.email}
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge variant={
                            alert.severity === 'high' ? 'destructive' :
                            alert.severity === 'medium' ? 'secondary' :
                            'outline'
                          }>
                            {alert.severity.toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-2 opacity-30 text-green-500" />
                    <p className="text-green-600">No suspicious activity detected</p>
                    <p className="text-sm">Your system is secure</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              {editingRole ? 'Edit Role' : 'Create Custom Role'}
            </DialogTitle>
            <DialogDescription>
              Define role details and assign permissions for each module
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Sales Manager"
                  data-testid="role-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={roleForm.color}
                  onValueChange={(value) => setRoleForm(prev => ({ ...prev, color: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(ROLE_COLORS).map((color) => (
                      <SelectItem key={color} value={color}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${ROLE_COLORS[color].split(' ')[0]}`} />
                          {color.charAt(0).toUpperCase() + color.slice(1)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this role's responsibilities"
                data-testid="role-description-input"
              />
            </div>

            {/* Permissions Matrix */}
            <div className="space-y-3">
              <Label>Permissions</Label>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="grid grid-cols-5 gap-2 p-3 bg-muted/50 text-sm font-medium">
                  <div>Module</div>
                  {PERMISSIONS.map((perm) => (
                    <div key={perm} className="text-center capitalize">{perm}</div>
                  ))}
                </div>
                {MODULES.map((module) => (
                  <div 
                    key={module.id}
                    className="grid grid-cols-5 gap-2 p-3 border-t border-border items-center"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <module.icon className="w-4 h-4 text-muted-foreground" />
                      {module.name}
                    </div>
                    {PERMISSIONS.map((perm) => (
                      <div key={perm} className="flex justify-center">
                        <Checkbox
                          checked={(roleForm.permissions[module.id] || []).includes(perm)}
                          onCheckedChange={() => togglePermission(module.id, perm)}
                          data-testid={`perm-${module.id}-${perm}`}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRole}
              disabled={!roleForm.name || !roleForm.description}
              data-testid="save-role-btn"
            >
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock Account Dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Lock className="w-5 h-5" />
              Lock Account
            </DialogTitle>
            <DialogDescription>
              Lock the account for <strong>{selectedUser?.email}</strong>. 
              The user will not be able to log in until unlocked.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label>Reason (optional)</Label>
            <Input
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              placeholder="e.g., Security concern, Policy violation"
              className="mt-2"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLockUser} data-testid="confirm-lock-btn">
              <Lock className="w-4 h-4 mr-2" />
              Lock Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Reset password for <strong>{selectedUser?.email}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <PasswordInput
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground">
                Minimum 8 characters recommended
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={requireChangeOnLogin}
                onCheckedChange={setRequireChangeOnLogin}
              />
              <Label>Require password change on next login</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordReset}
              disabled={!newPassword || newPassword.length < 8}
              data-testid="confirm-reset-pwd-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
